import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "next-themes";
import { Package, ClipboardList, Warehouse, Tag, LogOut, Menu, Globe,
         User, Sun, Moon, LayoutDashboard, X, BoxIcon, TriangleAlert, ShieldCheck } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LANG_TOGGLE, LOGOUT_BTN, THEME_TOGGLE } from "@/constants/testIds";
import { SCREEN_PATHS, hasScreen, roleBadgeKey } from "@/constants/screens";
import { visibleAdminTabs } from "@/constants/adminTabs";
import { useNotificationCenter } from "@/hooks/useNotificationCenter";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { NotificationBell, NotificationToaster } from "@/components/NotificationCenter";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import WhatsNewDialog from "@/components/WhatsNewDialog";
import { APP_VERSION, releasesFor } from "@/releaseNotes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_VERSION_KEY = "boxprep.seenVersion";

function readSeenVersion() {
  try { return localStorage.getItem(SEEN_VERSION_KEY); } catch { return null; }
}

/* ── Nav configuration ─────────────────────────────────── */
const MAIN_NAV = [
  { screen: "dashboard",              icon: LayoutDashboard },
  { screen: "sender_warehouse",       icon: Package },
  { screen: "planning",               icon: ClipboardList },
  { screen: "receiver_warehouse",     icon: Warehouse },
  { screen: "marketplace_amazon_fba", icon: Tag },
];

const ROLE_BADGE = {
  "role.warehouse_staff": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  "role.tenant_member":  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  "role.tenant_admin":   "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  "role.platform_admin": "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
};

function NavItem({ path, icon: Icon, label, onClick }) {
  const location = useLocation();
  // NavLink's own isActive ignores query strings, but the admin links share
  // the same /admin pathname and differ only by ?tab=, so compare full URLs.
  const isActive = `${location.pathname}${location.search}` === path;
  return (
    <NavLink to={path} onClick={onClick}
      className={
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-[0.97] ${
          isActive
            ? "bg-blue-600 text-white shadow-sm"
            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200"
        }`
      }>
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function Sidebar({ onClose, center }) {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const visibleMain = MAIN_NAV.filter(item => hasScreen(user, item.screen));
  const adminTabs = visibleAdminTabs(user);
  const badgeKey = roleBadgeKey(user?.role);
  const warehouseNames = (user?.warehouse_assignments || []).map(a => a.name).join(", ");
  const releases = releasesFor(user);
  const latestRelease = releases[0]?.version;
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [seenVersion, setSeenVersion] = useState(readSeenVersion);

  const openWhatsNew = () => {
    setWhatsNewOpen(true);
    if (!latestRelease) return;
    setSeenVersion(latestRelease);
    try { localStorage.setItem(SEEN_VERSION_KEY, latestRelease); } catch { /* storage blocked: the dot shows again next visit */ }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // The session may still be valid, so stay signed in instead of showing the login page.
      toast.error(t("adm.error"));
      return;
    }
    navigate("/login");
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <BoxIcon className="w-4.5 h-4.5 text-white w-5 h-5" />
          </div>
          <span className="font-extrabold text-lg text-slate-900 dark:text-white tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}>
            BoxPrep
          </span>
        </div>
        {center && <NotificationBell center={center} className="hidden md:block" />}
        {onClose && (
          <button onClick={onClose}
            className="md:hidden p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {visibleMain.map(item => (
          <NavItem key={item.screen} path={SCREEN_PATHS[item.screen]}
            icon={item.icon} label={t(`nav.${item.screen}`)} onClick={onClose} />
        ))}

        {adminTabs.length > 0 && (
          <>
            <div className="pt-4 pb-1 px-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-600">
                {t("nav.management")}
              </span>
            </div>
            {adminTabs.map(item => (
              <NavItem key={item.tab} path={`/admin?tab=${item.tab}`}
                icon={item.icon} label={t(`adm.tab.${item.tab}`)} onClick={onClose} />
            ))}
          </>
        )}
      </nav>

      {/* Bottom controls */}
      <div className="p-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
        {/* Lang + Theme row */}
        <div className="flex gap-2">
          <button data-testid={LANG_TOGGLE} onClick={toggle}
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300 transition-all">
            <Globe className="w-3.5 h-3.5" />
            {lang.toUpperCase()}
          </button>
          <button data-testid={THEME_TOGGLE}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex items-center justify-center w-10 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-all">
            {theme === "dark"
              ? <Sun className="w-4 h-4 text-amber-400" />
              : <Moon className="w-4 h-4" />}
          </button>
        </div>

        <button onClick={openWhatsNew}
          className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
          v{APP_VERSION} · {t("whatsnew.button")}
          {latestRelease && latestRelease !== seenVersion && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600">
              <span className="sr-only">{t("whatsnew.new")}</span>
            </span>
          )}
        </button>

        {/* User row */}
        <div className="flex items-center gap-2 p-2 rounded-xl bg-slate-50 dark:bg-slate-800">
          <div className="w-7 h-7 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate leading-none">{user?.name}</p>
            <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${ROLE_BADGE[badgeKey] || ""}`}>
              {t(badgeKey)}
            </span>
            {warehouseNames && (
              <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{warehouseNames}</p>
            )}
          </div>
          <button data-testid={LOGOUT_BTN} onClick={handleLogout}
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 transition-colors shrink-0"
            title={t("nav.logout")}>
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
      <WhatsNewDialog open={whatsNewOpen} onOpenChange={setWhatsNewOpen} releases={releases} />
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const { t } = useLang();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Tenant members, warehouse staff with an assignment and the platform administrator (backup failures) get notifications.
  const hasFeed = Boolean(user?.membership) || (user?.warehouse_assignments || []).length > 0 || user?.role === "TOOLS_USER";
  const center = useNotificationCenter({ enabled: hasFeed });

  // Maintenance mode (DB restore) blocks writes: a banner warns before a save fails, so nobody types work that is lost.
  const [maintenance, setMaintenance] = useState(false);
  const signedIn = Boolean(user);
  const loadMaintenance = useCallback(async () => {
    if (!signedIn) return; // Layout mounts before the session check ends; no 401 noise meanwhile.
    const { data } = await axios.get(`${API}/system/maintenance`, { withCredentials: true });
    setMaintenance(data.maintenance);
  }, [signedIn]);
  useEffect(() => { loadMaintenance().catch(() => {}); }, [loadMaintenance]);
  useAutoRefresh(loadMaintenance);

  // A write that still hits the 503 turns the banner on at once; the screen's own error toast shows the translated text.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (error.response?.status === 503) {
        setMaintenance(true);
        error.response.data = { detail: t("maintenance.toast") };
      }
      throw error;
    });
    return () => axios.interceptors.response.eject(id);
  }, [t]);

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      {/* One toast host for page messages and notifications: bottom right (full width on phones), clear of the header controls. */}
      <Toaster position="bottom-right" richColors closeButton visibleToasts={3} />
      {hasFeed && <NotificationToaster center={center} />}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar — fixed on mobile (z-50), static on desktop */}
      <aside className={`
        fixed top-0 left-0 h-full w-56 z-50
        transform transition-transform duration-200
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        md:relative md:translate-x-0 md:z-auto md:shrink-0
      `}>
        <Sidebar onClose={() => setSidebarOpen(false)} center={hasFeed ? center : null} />
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar */}
        <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
          <button onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 transition-all">
            <Menu className="w-5 h-5 text-slate-700 dark:text-slate-300" />
          </button>
          <span className="font-extrabold text-base text-slate-900 dark:text-white"
            style={{ fontFamily: "var(--font-heading)" }}>BoxPrep</span>
          {hasFeed ? <NotificationBell center={center} /> : <div className="w-9" />}
        </header>

        {/* Same red as the others' banner, but with a shield: only the platform administrator can still write. */}
        {maintenance && (user?.role === "TOOLS_USER" ? (
          <div role="status" className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-700 text-white border-b-4 border-amber-300 shrink-0">
            <ShieldCheck className="w-5 h-5 shrink-0" />
            <span>{t("maintenance.banner_platform")}</span>
          </div>
        ) : (
          <div role="alert" className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-red-600 text-white shrink-0">
            <TriangleAlert className="w-5 h-5 shrink-0" />
            <span>{t("maintenance.banner")}</span>
          </div>
        ))}

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
